import React, { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '../../../components/ui'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  UserGroupIcon,
  CalendarIcon,
  MapPinIcon,
  StarIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  ClockIcon,
  ClipboardDocumentIcon,
  DocumentIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { useProvider, useApproveProvider, useRejectProvider } from '../../../hooks/useProviders'
import { useAdminAllDocuments } from '../../../hooks/useDocuments'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { ServiceProvider, DocumentStatus } from '../../../types'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

const serviceTypeLabels: Record<string, string> = {
  // Actual database ServiceType enum values
  AMBULANCE_TRANSPORT: 'Ambulance Transport',
  GHUSL_SERVICE: 'Ghusl Service',
  KAFAN_SERVICE: 'Kafan Service',
  JANAZAH_LEAD: 'Janazah Lead',
  BURIAL_PLOT: 'Burial Plot',
  CATERING: 'Catering',
  FLOWERS: 'Flowers',
  TENT_RENTAL: 'Tent Rental',
  PHOTOGRAPHY: 'Photography',
  YASIN_BOOKS: 'Yasin Books',
  ISLAMIC_SOUVENIRS: 'Islamic Souvenirs',
  // Legacy mappings for backward compatibility
  AMBULANCE_PROVIDER: 'Ambulance',
  USTADZ: 'Ustadz',
  CEMETERY_PROVIDER: 'Cemetery',
  VENDOR: 'Vendor',
  PROVIDER: 'General Provider',
}

const serviceTypeColors: Record<string, string> = {
  // Actual database ServiceType enum values
  AMBULANCE_TRANSPORT: 'bg-red-100 text-red-800',
  GHUSL_SERVICE: 'bg-green-100 text-green-800',
  KAFAN_SERVICE: 'bg-blue-100 text-blue-800',
  JANAZAH_LEAD: 'bg-purple-100 text-purple-800',
  BURIAL_PLOT: 'bg-gray-100 text-gray-800',
  CATERING: 'bg-orange-100 text-orange-800',
  FLOWERS: 'bg-pink-100 text-pink-800',
  TENT_RENTAL: 'bg-yellow-100 text-yellow-800',
  PHOTOGRAPHY: 'bg-indigo-100 text-indigo-800',
  YASIN_BOOKS: 'bg-teal-100 text-teal-800',
  ISLAMIC_SOUVENIRS: 'bg-emerald-100 text-emerald-800',
  // Legacy mappings for backward compatibility
  AMBULANCE_PROVIDER: 'bg-blue-100 text-blue-800',
  USTADZ: 'bg-purple-100 text-purple-800',
  CEMETERY_PROVIDER: 'bg-green-100 text-green-800',
  VENDOR: 'bg-orange-100 text-orange-800',
  PROVIDER: 'bg-gray-100 text-gray-800',
}

const documentTypeLabels: Record<string, string> = {
  BUSINESS_LICENSE: 'Business License',
  PROFESSIONAL_CERTIFICATE: 'Professional Certificate',
  INSURANCE_DOCUMENT: 'Insurance Document',
  IDENTIFICATION: 'Identification Document',
  OPERATING_PERMIT: 'Operating Permit',
  VEHICLE_REGISTRATION: 'Vehicle Registration',
  SERVICE_CERTIFICATION: 'Service Certification',
}

const documentStatusColors: Record<DocumentStatus, string> = {
  UPLOADED: 'bg-yellow-100 text-yellow-800',
  PENDING: 'bg-blue-100 text-blue-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'UPLOADED':
      return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'VERIFIED':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'PENDING':
      return <ClockIcon className="h-4 w-4" />
    case 'VERIFIED':
      return <CheckCircleIcon className="h-4 w-4" />
    default:
      return <DocumentTextIcon className="h-4 w-4" />
  }
}

export default function ProviderDetailsPage() {
  const { providerId } = useParams<{ providerId: string }>()
  const navigate = useNavigate()
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<any>(null)
  const [showDocumentModal, setShowDocumentModal] = useState(false)
  const [documentPreview, setDocumentPreview] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const { data: provider, isLoading: providerLoading, error: providerError } = useProvider(providerId!)
  const { data: documents = [], isLoading: documentsLoading } = useAdminAllDocuments()
  const approveProviderMutation = useApproveProvider()
  const rejectProviderMutation = useRejectProvider()

  // Filter documents for this specific provider
  const providerDocuments = documents.filter(doc =>
    doc.serviceProvider?.id === providerId
  )

  const isLoading = providerLoading || documentsLoading

  // Check document compliance for the provider's service type
  const checkDocumentCompliance = () => {
    if (!provider || !providerDocuments.length) {
      return {
        isCompliant: false,
        missingDocuments: [],
        unverifiedDocuments: [],
        totalRequired: 0,
        totalVerified: 0
      }
    }

    // Define required documents by service type (matching backend logic)
    const getRequiredDocuments = (serviceType: string) => {
      switch (serviceType) {
        case 'AMBULANCE_TRANSPORT':
          return ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT', 'IDENTIFICATION']
        case 'GHUSL_SERVICE':
          return ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE']
        case 'KAFAN_SERVICE':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'JANAZAH_LEAD':
          return ['PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION']
        case 'BURIAL_PLOT':
          return ['BUSINESS_LICENSE', 'OPERATING_PERMIT', 'IDENTIFICATION']
        case 'CATERING':
          return ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT']
        case 'FLOWERS':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'TENT_RENTAL':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'PHOTOGRAPHY':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'PRINTING':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'ISLAMIC_SOUVENIRS':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        default:
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
      }
    }

    const requiredDocuments = getRequiredDocuments(provider.serviceType)
    const uploadedDocuments = providerDocuments.map(doc => doc.type)

    // Find missing required documents
    const missingDocuments = requiredDocuments.filter(docType => !uploadedDocuments.includes(docType))

    // Find unverified documents (uploaded but not verified)
    const unverifiedDocuments = providerDocuments
      .filter(doc => doc.status !== 'VERIFIED')
      .map(doc => doc.type)

    const totalVerified = providerDocuments.filter(doc => doc.status === 'VERIFIED').length

    return {
      isCompliant: missingDocuments.length === 0 && unverifiedDocuments.length === 0,
      missingDocuments,
      unverifiedDocuments,
      totalRequired: requiredDocuments.length,
      totalVerified
    }
  }

  const handleApprove = async () => {
    if (!providerId) return

    try {
      await approveProviderMutation.mutateAsync(providerId)
      toast.success('Provider approved successfully')
      navigate('/admin/providers/pending')
    } catch (error: any) {
      console.error('Failed to approve provider:', error)

      // Check for detailed document verification errors
      if (error.response?.data?.message?.includes('documents are missing or not verified')) {
        const errorData = error.response.data
        const missingDocs = errorData.missingDocuments || []
        const unverifiedDocs = errorData.unverifiedDocuments || []

        let errorMessage = 'Provider cannot be approved until document requirements are met:\n\n'

        if (missingDocs.length > 0) {
          errorMessage += `Missing required documents: ${missingDocs.join(', ')}\n`
        }

        if (unverifiedDocs.length > 0) {
          errorMessage += `Unverified documents: ${unverifiedDocs.join(', ')}\n`
        }

        errorMessage += '\nPlease verify all required documents before approving.'

        toast.error(errorMessage, { duration: 8000 })
      } else {
        toast.error(error.response?.data?.message || 'Failed to approve provider')
      }
    }
  }

  const handleReject = async (reason: string) => {
    if (!providerId) return

    try {
      await rejectProviderMutation.mutateAsync({ providerId, reason })
      toast.success('Provider application rejected')
      setShowRejectModal(false)
      setRejectionReason('')
      navigate('/admin/providers/pending')
    } catch (error) {
      console.error('Failed to reject provider:', error)
    }
  }

  const openRejectModal = () => {
    setShowRejectModal(true)
  }

  const closeRejectModal = () => {
    setShowRejectModal(false)
    setRejectionReason('')
  }

  
  const downloadDocument = async (documentFile: any) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const response = await fetch(`${API_BASE_URL}/api/documents/provider/${documentFile.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.status} ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = window.document.createElement('a')
      a.href = url
      a.download = documentFile.originalName || documentFile.fileName
      window.document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      window.document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download document:', error)
      toast.error('Failed to download document')
    }
  }

  const loadDocumentPreview = async (document: any) => {
    setPreviewLoading(true)
    try {
      console.log('Loading document preview for:', document)
      console.log('Document ID:', document.id)
      console.log('Document mimeType:', document.mimeType)

      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const response = await fetch(`${API_BASE_URL}/api/documents/provider/${document.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': document.mimeType || 'application/octet-stream'
        }
      })
      console.log('Fetch response status:', response.status)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`)
      }

      const blob = await response.blob()
      console.log('Blob type:', blob.type)
      console.log('Blob size:', blob.size)

      // Check if it's an image or PDF
      const fileType = document.mimeType || blob.type
      if (fileType.startsWith('image/')) {
        const url = URL.createObjectURL(blob)
        console.log('Created image blob URL:', url)
        setDocumentPreview(url)
        return () => URL.revokeObjectURL(url)
      } else if (fileType === 'application/pdf') {
        const url = URL.createObjectURL(blob)
        console.log('Created PDF blob URL:', url)
        setDocumentPreview(url)
        return () => URL.revokeObjectURL(url)
      } else {
        console.log('File type not previewable:', fileType)
        setDocumentPreview(null) // Not previewable
      }
    } catch (error) {
      console.error('Failed to load document preview:', error)
      setDocumentPreview(null)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleViewDocument = (document: any) => {
    setSelectedDocument(document)
    loadDocumentPreview(document)
    setShowDocumentModal(true)
  }

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false)
    setSelectedDocument(null)
    if (documentPreview) {
      URL.revokeObjectURL(documentPreview)
      setDocumentPreview(null)
    }
  }

  const handleCopyProviderId = async () => {
    if (!provider?.id) return
    try {
      await navigator.clipboard.writeText(provider.id)
      toast.success('Provider ID copied to clipboard')
    } catch (err) {
      console.error('Failed to copy provider ID:', err)
      toast.error('Failed to copy provider ID')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (providerError || !provider) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-medium">Failed to load provider details</div>
        <p className="text-gray-500 mt-2">Please try again later</p>
        <Link
          to="/admin/providers/pending"
          className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Pending Providers
        </Link>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div>
            <div className="flex items-center space-x-4">
              <Link
                to="/admin/providers/pending"
                className="text-primary-600 hover:text-primary-700 font-medium flex items-center"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Pending Providers
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Provider Verification Details</h1>
                <p className="text-gray-600 mt-1">Review provider application and documents</p>
              </div>
            </div>
          </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Provider Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Provider Information</h2>
                <button
                  onClick={handleCopyProviderId}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                  title="Copy Provider ID"
                >
                  <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                  ID: {provider.id.substring(0, 8)}...
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <UserGroupIcon className="h-5 w-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Name</p>
                      <p className="text-base text-gray-900">{provider.user.firstName} {provider.user.lastName}</p>
                    </div>

                    <div className="flex items-center">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Email</p>
                        <p className="text-base text-gray-900">{provider.user.email}</p>
                      </div>
                    </div>

                    {provider.user.phone && (
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Phone</p>
                          <p className="text-base text-gray-900">{provider.user.phone}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Applied</p>
                        <p className="text-base text-gray-900">
                          {new Date(provider.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <BuildingOfficeIcon className="h-5 w-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Business Information</h3>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Business Name</p>
                      <p className="text-base text-gray-900">{provider.companyName}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-500">Service Type</p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${serviceTypeColors[provider.serviceType]}`}>
                        {serviceTypeLabels[provider.serviceType]}
                      </span>
                    </div>

                    {provider.city && (
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Location</p>
                          <p className="text-base text-gray-900">{provider.city}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Account Status</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          provider.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {provider.verified ? 'Verified' : 'Not Verified'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {provider.description && (
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <InformationCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">Business Description</h3>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{provider.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Document Compliance Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <ShieldCheckIcon className="h-5 w-5 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Document Compliance Status</h2>
              </div>

              {(() => {
                const compliance = checkDocumentCompliance()
                return (
                  <div className="space-y-4">
                    {/* Overall Status */}
                    <div className={`border rounded-lg p-4 ${
                      compliance.isCompliant
                        ? 'border-green-200 bg-green-50'
                        : 'border-yellow-200 bg-yellow-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {compliance.isCompliant ? (
                            <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
                          ) : (
                            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3" />
                          )}
                          <div>
                            <h3 className={`text-lg font-medium ${
                              compliance.isCompliant ? 'text-green-900' : 'text-yellow-900'
                            }`}>
                              {compliance.isCompliant ? 'Document Requirements Met' : 'Document Requirements Not Met'}
                            </h3>
                            <p className={`text-sm mt-1 ${
                              compliance.isCompliant ? 'text-green-700' : 'text-yellow-700'
                            }`}>
                              {compliance.isCompliant
                                ? 'All required documents have been uploaded and verified. This provider can be approved.'
                                : 'This provider cannot be approved until document requirements are satisfied.'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <DocumentIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <h4 className="font-medium text-gray-900">Required Documents</h4>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 mt-2">{compliance.totalRequired}</p>
                        <p className="text-sm text-gray-600">Total required for {serviceTypeLabels[provider?.serviceType] || provider?.serviceType}</p>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <CheckBadgeIcon className="h-5 w-5 text-green-500 mr-2" />
                          <h4 className="font-medium text-gray-900">Verified</h4>
                        </div>
                        <p className="text-2xl font-bold text-green-600 mt-2">{compliance.totalVerified}</p>
                        <p className="text-sm text-gray-600">Successfully verified</p>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <ClockIcon className="h-5 w-5 text-yellow-500 mr-2" />
                          <h4 className="font-medium text-gray-900">Still Needed</h4>
                        </div>
                        <p className="text-2xl font-bold text-yellow-600 mt-2">
                          {compliance.missingDocuments.length + compliance.unverifiedDocuments.length}
                        </p>
                        <p className="text-sm text-gray-600">Missing or unverified</p>
                      </div>
                    </div>

                    {/* Missing/Unverified Details */}
                    {!compliance.isCompliant && (
                      <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                        <h4 className="font-medium text-yellow-900 mb-3">Action Required:</h4>

                        {compliance.missingDocuments.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-yellow-800 mb-1">Missing Documents:</p>
                            <ul className="text-sm text-yellow-700 space-y-1">
                              {compliance.missingDocuments.map(docType => (
                                <li key={docType} className="flex items-center">
                                  <XCircleIcon className="h-4 w-4 text-red-500 mr-2" />
                                  {documentTypeLabels[docType] || docType}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {compliance.unverifiedDocuments.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-yellow-800 mb-1">Unverified Documents:</p>
                            <ul className="text-sm text-yellow-700 space-y-1">
                              {compliance.unverifiedDocuments.map(docType => (
                                <li key={docType} className="flex items-center">
                                  <ClockIcon className="h-4 w-4 text-yellow-500 mr-2" />
                                  {documentTypeLabels[docType] || docType}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mt-3 p-3 bg-yellow-100 rounded-md">
                          <p className="text-xs text-yellow-800">
                            <strong>Note:</strong> You can verify individual documents in the{' '}
                            <a href="/admin/providers/documents" className="underline">
                              Document Verification page
                            </a>{' '}
                            before approving this provider.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>

          {/* Documents Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <DocumentIcon className="h-5 w-5 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Uploaded Documents</h2>
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {providerDocuments.length} documents
                </span>
              </div>

              {providerDocuments.length === 0 ? (
                <div className="text-center py-8">
                  <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No documents uploaded</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This provider hasn't uploaded any documents yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {providerDocuments.map((document) => (
                    <div key={document.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-medium text-gray-900">
                              {documentTypeLabels[document.type] || document.type}
                            </h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${documentStatusColors[document.status]}`}>
                              {document.status}
                            </span>
                          </div>

                          <div className="mt-2 text-sm text-gray-600">
                            <p>File: {document.originalName || document.fileName}</p>
                            <p>Size: {(document.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                            <p>Uploaded: {new Date(document.uploadedAt).toLocaleDateString()}</p>
                            {document.verificationNotes && (
                              <p className="mt-1 text-gray-500">Notes: {document.verificationNotes}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleViewDocument(document)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => downloadDocument(document)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {(() => {
                const compliance = checkDocumentCompliance()
                const canApprove = compliance.isCompliant

                return (
                  <>
                    {/* Document Compliance Indicator */}
                    <div className={`border rounded-lg p-3 text-sm ${
                      canApprove
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-yellow-200 bg-yellow-50 text-yellow-800'
                    }`}>
                      <div className="flex items-center">
                        {canApprove ? (
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2" />
                        ) : (
                          <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600 mr-2" />
                        )}
                        <span className="font-medium">
                          {canApprove ? 'Ready for Approval' : 'Document Issues Found'}
                        </span>
                      </div>
                      {!canApprove && (
                        <p className="text-xs mt-1 ml-6">
                          {compliance.missingDocuments.length + compliance.unverifiedDocuments.length} document(s) need attention
                        </p>
                      )}
                    </div>

                    <button
                      onClick={handleApprove}
                      disabled={approveProviderMutation.isPending || !canApprove}
                      className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                        canApprove
                          ? 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500'
                          : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <CheckBadgeIcon className="h-4 w-4 mr-2" />
                      {canApprove ? 'Approve Provider' : 'Documents Required'}
                    </button>

                    <button
                      onClick={openRejectModal}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <XCircleIcon className="h-4 w-4 mr-2" />
                      Reject Provider
                    </button>
                  </>
                )
              })()}
            </div>
          </div>

          {/* Document Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Document Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Documents</span>
                <span className="text-sm font-medium text-gray-900">{providerDocuments.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Verified</span>
                <span className="text-sm font-medium text-green-600">
                  {providerDocuments.filter(d => d.status === 'VERIFIED').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Pending</span>
                <span className="text-sm font-medium text-yellow-600">
                  {providerDocuments.filter(d => d.status === 'PENDING' || d.status === 'UPLOADED').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Rejected</span>
                <span className="text-sm font-medium text-red-600">
                  {providerDocuments.filter(d => d.status === 'REJECTED').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Reject Provider Application
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to reject the application from {provider.companyName}?
                        This action cannot be undone.
                      </p>
                    </div>
                    <div className="mt-4">
                      <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700">
                        Rejection Reason
                      </label>
                      <textarea
                        id="rejection-reason"
                        rows={3}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Please provide a reason for rejection..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => handleReject(rejectionReason)}
                  disabled={!rejectionReason.trim() || rejectProviderMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Reject Application
                </button>
                <button
                  type="button"
                  onClick={closeRejectModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document View Modal */}
      {showDocumentModal && selectedDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseDocumentModal}></div>

            <div className="relative inline-block w-full max-w-4xl p-6 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Document - {selectedDocument.originalName || selectedDocument.fileName}
                </h3>
                <button onClick={handleCloseDocumentModal} className="text-gray-400 hover:text-gray-500">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Document Type */}
                  <div>
                    <p className="text-sm font-medium text-gray-500">Document Type</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {documentTypeLabels[selectedDocument.type] || selectedDocument.type?.replace(/_/g, ' ') || 'Unknown'}
                    </p>
                  </div>
                  {/* Status */}
                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedDocument.status?.replace(/_/g, ' ') || 'Unknown'}
                    </p>
                  </div>
                  {/* Provider Name */}
                  <div>
                    <p className="text-sm font-medium text-gray-500">Provider</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedDocument.serviceProvider?.companyName ||
                       `${selectedDocument.serviceProvider?.user?.firstName || ''} ${selectedDocument.serviceProvider?.user?.lastName || ''}`.trim() ||
                       'Unknown Provider'}
                    </p>
                  </div>
                  {/* Uploaded Date */}
                  <div>
                    <p className="text-sm font-medium text-gray-500">Uploaded Date</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(selectedDocument.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Verification Notes */}
                {selectedDocument.verificationNotes && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Verification Notes</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedDocument.verificationNotes}
                    </p>
                  </div>
                )}

                {/* Document Preview */}
                {documentPreview ? (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Document Preview</p>
                    <div className="border rounded-lg overflow-hidden">
                      {selectedDocument.mimeType === 'application/pdf' ? (
                        <iframe
                          src={documentPreview}
                          className="w-full h-96 border-0"
                          title={`PDF preview: ${selectedDocument.originalName || selectedDocument.fileName}`}
                        />
                      ) : selectedDocument.mimeType?.startsWith('image/') ? (
                        <img
                          src={documentPreview}
                          alt="Document preview"
                          className="w-full h-auto max-h-96 object-contain bg-gray-50"
                        />
                      ) : (
                        <div className="w-full h-96 flex items-center justify-center bg-gray-50">
                          <div className="text-center">
                            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-500">Preview not available for this file type</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <button
                        onClick={() => downloadDocument(selectedDocument)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Open document in new tab →
                      </button>
                    </div>
                  </div>
                ) : previewLoading ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-sm text-gray-500">Loading preview...</p>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No preview available</p>
                    <button
                      onClick={() => downloadDocument(selectedDocument)}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Download document →
                    </button>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseDocumentModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}